import {
    applyTranslations,
    getLocale,
    onLocaleChange,
    setLocale,
    t,
    type Locale,
} from './i18n';
import { saveSession, state } from './state';
import { apiRequest } from './api';
import { navigateTo, updateTitle } from './router';
import { setupStreamsBanner } from './streams';
import { mountNotifications, setupNotifications } from './notifications';

let rolesLoadingPromise: Promise<void> | null = null;

/**
 * Гарантирует, что роли пользователя загружены в state.user.
 * Если запрос уже идёт — ждёт его. Если роли уже есть — сразу возвращает.
 */
export async function ensureRolesLoaded(): Promise<void> {
  // Нет токена — нечего грузить
  if (!state.token || !state.user) return;

  // Если роли уже есть — не тратим запрос
  if (
    typeof state.user.is_moderator === 'boolean' &&
    typeof state.user.can_rate === 'boolean'
  ) return;

  // Если запрос уже идёт — ждём его
  if (rolesLoadingPromise) return rolesLoadingPromise;

  rolesLoadingPromise = (async () => {
    try {
      const me = await apiRequest<{
        user: {
          id: string;
          email: string;
          username: string | null;
          is_moderator: boolean;
          is_admin: boolean;
          is_ghost: boolean;
          can_rate: boolean;
          player_id: number | null;
          player_name: string | null;
        };
      }>('/api/auth/me', { token: state.token });

      saveSession(
        {
          id: me.user.id,
          email: me.user.email,
          username: me.user.username,
          is_moderator: me.user.is_moderator,
          is_admin: me.user.is_admin,
          is_ghost: me.user.is_ghost,
          can_rate: me.user.can_rate,
          player_id: me.user.player_id ?? null,
          player_name: me.user.player_name ?? null,
        },
        state.token!,
      );

      renderUserBox();
    } catch (err) {
      console.warn('[app] failed to load roles:', err);
    } finally {
      rolesLoadingPromise = null;
    }
  })();

  return rolesLoadingPromise;
}

/**
 * Инициализация общего UI: переключатель языка, renderUserBox, footer.
 * Вызывается один раз при старте приложения.
 */
export function setupApp(): void {
    setupLangSwitch();
    setupNotifications();
    renderUserBox();
    setupFooterDiscordCopy();
    setupSupportButton();
    setupStreamsBanner();

    window.addEventListener('session:changed', () => {
        renderUserBox();
    });

    onLocaleChange(() => {
        applyTranslations();
        renderUserBox();
        updateTitle();
    });
}

/**
 * Рендерит шапку (user-box).
 */
export function renderUserBox(): void {
    const box = document.getElementById('user-box');
    if (!box) return;
    box.innerHTML = '';

    if (state.user) {
        // «Режим стримера» — в шапке для модераторов/ghost/админа (в «Инструментах» тоже остаётся)
        const isStaff = Boolean(state.user.is_moderator || state.user.is_admin || state.user.is_ghost);

        // Ник — кнопка входа в личный профиль (/profile)
        const nameWrap = document.createElement('a');
        nameWrap.href = '/profile';
        nameWrap.className = 'topbar-link topbar-link--nick';
        nameWrap.title = t('topbar.my_profile');
        nameWrap.setAttribute('aria-label', t('topbar.my_profile'));

        const nameLabel = document.createElement('span');
        nameLabel.className = 'topbar-username-label';
        nameLabel.textContent = state.user.username || state.user.email;

        // Кружок с первой буквой ника — кнопка профиля читается мгновенно
        const avatar = document.createElement('span');
        avatar.className = 'topbar-avatar';
        avatar.textContent = (state.user.username || state.user.email || '?')
            .charAt(0)
            .toUpperCase();
        avatar.setAttribute('aria-hidden', 'true');

        nameWrap.append(avatar, nameLabel);

        if (state.user.is_admin) {
            const badge = document.createElement('span');
            badge.className = 'role-badge role-badge--admin';
            badge.textContent = `★ ${t('topbar.role_admin')}`;
            nameWrap.appendChild(badge);
        } else if (state.user.is_ghost) {
            const badge = document.createElement('span');
            badge.className = 'role-badge role-badge--ghost';
            badge.textContent = `💠 ${t('topbar.role_ghost')}`;
            nameWrap.appendChild(badge);
        } else if (state.user.is_moderator) {
            const badge = document.createElement('span');
            badge.className = 'role-badge role-badge--moderator';
            badge.textContent = `◆ ${t('topbar.role_moderator')}`;
            nameWrap.appendChild(badge);
        }

        // Кнопка админа
        let adminLink: HTMLAnchorElement | null = null;
        if (state.user.is_admin) {
            adminLink = document.createElement('a');
            adminLink.href = '/admin';
            adminLink.className = 'topbar-link topbar-link--admin';
            adminLink.innerHTML = `⚙ <span class="btn-label">${t('topbar.admin')}</span>`;
        }

        // «Выйти» переехало в личный профиль (/profile)
        if (adminLink) box.append(adminLink);

        // Режим стримера — только персоналу лиги
        if (isStaff) {
            const controlLink = document.createElement('a');
            controlLink.href = '/control';
            controlLink.className = 'topbar-link topbar-link--streamer';
            controlLink.title = t('topbar.streamer_mode');
            controlLink.innerHTML = `🎬 <span class="btn-label">${t('topbar.streamer_mode')}</span>`;
            box.append(controlLink);
        }

        // Колокольчик уведомлений — перед кнопкой профиля.
        mountNotifications(box);

        // Личная статистика — иконка графиков с подписью рядом с профилем.
        const statsLink = document.createElement('a');
        statsLink.href = '/my-stats';
        statsLink.className = 'topbar-link topbar-link--stats';
        statsLink.title = t('topbar.my_stats');
        statsLink.setAttribute('aria-label', t('topbar.my_stats'));
        // Иконка столбиков (без свечения — см. правила по иконкам).
        statsLink.innerHTML = `
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                <path d="M4 20V11M10 20V5M16 20V14M22 20H2"
                    fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
            </svg><span class="btn-label">${t('topbar.my_stats')}</span>`;
        box.append(statsLink);

        box.append(nameWrap);
        return;
    }

    // Не залогинен — кнопка «Войти»
    const loginBtn = document.createElement('button');
    loginBtn.type = 'button';
    loginBtn.className = 'topbar-login-btn';
    loginBtn.textContent = t('common.login');
    loginBtn.addEventListener('click', () => {
        navigateTo('/auth');
    });
    box.appendChild(loginBtn);
}

function setupLangSwitch(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('.lang-btn');

    const updateActive = (locale: Locale): void => {
        buttons.forEach((btn) => {
            btn.classList.toggle('is-active', btn.dataset.lang === locale);
        });
    };

    buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const lang = btn.dataset.lang as Locale | undefined;
            if (lang === 'ru' || lang === 'en') setLocale(lang);
        });
    });

    updateActive(getLocale());
    onLocaleChange(updateActive);
}

function setupFooterDiscordCopy(): void {
    const btn = document.getElementById('copy-discord');
    if (!btn) return;

    btn.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText('nerderror');
            const original = btn.textContent;
            btn.textContent = '✓ Скопировано';
            setTimeout(() => {
                btn.textContent = original;
            }, 1500);
        } catch { }
    });
}

/**
 * Плашка поддержки (на всех страницах) и кнопка в футере открывают страницу /support.
 * Плашку можно скрыть — выбор запоминается в localStorage.
 */
const SUPPORT_DISMISSED_KEY = 'support.banner.dismissed';

function setupSupportButton(): void {
  // Кнопка «Поддержать проект» в футере
  const footerBtn = document.getElementById('btn-support');
  if (footerBtn) {
    const fresh = footerBtn.cloneNode(true) as HTMLElement;
    footerBtn.replaceWith(fresh);
    fresh.addEventListener('click', () => navigateTo('/support'));
  }

  // Плашка о денежной поддержке
  const banner = document.getElementById('support-banner');
  if (!banner) return;

  let dismissed = false;
  try {
    dismissed = localStorage.getItem(SUPPORT_DISMISSED_KEY) === '1';
  } catch {
    /* localStorage недоступен — плашка просто не запоминается */
  }

  if (!dismissed) banner.classList.remove('hidden');

  const openBtn = document.getElementById('support-banner-open');
  openBtn?.addEventListener('click', () => navigateTo('/support'));

  const closeBtn = document.getElementById('support-banner-close');
  closeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    banner.classList.add('hidden');
    try {
      localStorage.setItem(SUPPORT_DISMISSED_KEY, '1');
    } catch {
      /* localStorage недоступен */
    }
  });
}